import { pricingFaq } from '@/content/faq';
import { type ReactNode } from 'react';
import { SiteMeta } from '@/components/site/SiteMeta';
import { usePlansCatalog } from '@/context/PlansContext';
import {
  checkoutHref,
  formatPlanChannels,
  formatPlanPrice,
  formatPlanPriceHint,
  formatPlanPriceLabel,
  formatPlanSeats,
  hasActivePromotion,
  isCustomQuotePlan,
  trialHref,
  type PublicPlan,
} from '@/content/plans';
import { openSupportChat, supportWhatsAppHref } from '@/lib/supportChat';

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
    <path d="M5 12l5 5L20 6" />
  </svg>
);
const X = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);
const Chevron = () => (
  <svg className="cv" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

function planFeatures(plan: PublicPlan): string[] {
  const items: string[] = [];
  items.push(formatPlanSeats(plan));
  items.push(formatPlanChannels(plan));
  if (plan.active_quota_monthly > 0) {
    items.push(`Até ${plan.active_quota_monthly.toLocaleString('pt-BR')} mensagens/mês`);
  }
  if (plan.extra_seat_monthly_cents != null && plan.extra_seat_monthly_cents > 0) {
    items.push(`Usuário extra: ${formatPlanPrice(plan.extra_seat_monthly_cents)}/mês`);
  }
  if (plan.extra_connection_monthly_cents != null && plan.extra_connection_monthly_cents > 0) {
    items.push(`Número extra: ${formatPlanPrice(plan.extra_connection_monthly_cents)}/mês`);
  }
  return items;
}

function PlanCard({ plan }: { plan: PublicPlan }) {
  const custom = isCustomQuotePlan(plan);
  const hint = formatPlanPriceHint(plan);
  return (
    <div className={`plan rev${plan.is_popular ? ' pop' : ''}`}>
      {plan.is_popular && <div className="pop-tag">Mais popular</div>}
      <div className="pn">{plan.name}</div>
      {hasActivePromotion(plan) ? (
        <div className="pd">
          <s style={{ color: 'var(--muted)' }}>{formatPlanPrice(plan.monthly_price_cents)}</s>{' '}
          <span
            style={{
              background: 'rgba(18,183,106,.12)',
              color: '#0f9d58',
              fontWeight: 700,
              fontSize: '.72rem',
              padding: '2px 8px',
              borderRadius: 6,
            }}
          >
            {plan.promotion?.name}
          </span>
        </div>
      ) : (
        <div className="pd">
          {custom ? 'Para operações com alto volume e vários setores.' : 'Assinatura mensal, sem fidelidade.'}
        </div>
      )}
      <div className="price">
        {hint && <span className="per">{hint}</span>}
        <span className="amt" style={custom ? { fontSize: '1.7rem' } : undefined}>
          {formatPlanPriceLabel(plan)}
        </span>
      </div>
      <ul className="plist">
        {planFeatures(plan).map((f) => (
          <li key={f}>
            <span className="pc">
              <Check />
            </span>
            {f}
          </li>
        ))}
      </ul>
      {custom ? (
        <a className="btn btn-primary" href="/solicitar-orcamento">
          Solicitar orçamento
        </a>
      ) : (
        <a className={`btn ${plan.is_popular ? 'btn-primary' : 'btn-ghost'}`} href={checkoutHref(plan.code)}>
          {plan.is_popular ? 'Quero este plano →' : 'Assinar agora'}
        </a>
      )}
    </div>
  );
}

function PlansGrid() {
  // Erro na atualização ao vivo não esconde planos válidos: o catálogo do build
  // continua certo e é melhor que uma mensagem de erro no lugar da tabela.
  const { sortedPlans, loading, reload } = usePlansCatalog();

  if (loading) {
    return (
      <div className="plans">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="plan" style={{ minHeight: 340, opacity: 0.5 }} aria-hidden>
            <div className="pn">Carregando…</div>
          </div>
        ))}
        <span className="sr-only">Carregando planos…</span>
      </div>
    );
  }
  if (sortedPlans.length === 0) {
    return (
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <p className="note" style={{ fontStyle: 'normal' }}>
          Não foi possível carregar os planos agora.
        </p>
        <button type="button" onClick={reload} className="btn btn-ghost" style={{ marginTop: 12 }}>
          Tentar de novo
        </button>
      </div>
    );
  }
  return (
    <div className="plans auto">
      {sortedPlans.map((plan) => (
        <PlanCard key={plan.id} plan={plan} />
      ))}
    </div>
  );
}

export function PrecosPage() {
  return (
    <>
      <SiteMeta
        title="Preços - Operax"
        description="Planos a partir de R$ 129,90/mês, sem fidelidade. O custo da Meta na mesa, sem taxa escondida."
        path="/precos"
      />

      <section className="page-hero">
        <div className="hero-grid-bg" />
        <div className="shell page-hero-in rev">
          <h1>Planos claros. Sem surpresa na fatura.</h1>
          <p className="lead">
            Assinatura mensal, cancele quando quiser. Todo plano já inclui o WhatsApp oficial da Meta e o CRM. Os apps
            você adiciona à parte, só quando forem úteis. Ou{' '}
            <a href="/calculadora" style={{ color: 'var(--blue)', fontWeight: 600 }}>
              calcule seu preço
            </a>{' '}
            com assentos, canais e add-ons.
          </p>
        </div>
      </section>

      <section className="pad pricing" style={{ paddingTop: 32 }}>
        <div className="shell">
          <PlansGrid />
          <p className="note">
            Preços e planos vêm do catálogo oficial. Quer montar com add-ons e extras antes de assinar?
            Use a calculadora.
          </p>
          <div className="rev" style={{ textAlign: 'center', marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            <a className="btn btn-ghost" href="/calculadora">
              Calcular meu preço
            </a>
            <a className="btn btn-primary" href={trialHref()}>
              Assinar agora →
            </a>
          </div>
        </div>
      </section>

      <section className="pad" style={{ background: '#fff', borderTop: '1px solid var(--line-2)' }}>
        <div className="shell">
          <div className="sec-head rev">
            <div className="eyebrow">Operax vs. o mercado</div>
            <h2 className="h2">
              O custo da Meta na mesa, <span className="accent">não embutido.</span>
            </h2>
            <p className="lead" style={{ marginTop: 16 }}>
              A maior parte do custo no WhatsApp é a taxa da Meta. A gente mostra ela separada da assinatura.
            </p>
          </div>
          <div className="cmp">
            <div className="cmp-col rev">
              <h3>O mercado</h3>
              <ul>
                <li>
                  <span className="mk no">
                    <X />
                  </span>
                  Taxa da Meta embutida, sem transparência
                </li>
                <li>
                  <span className="mk no">
                    <X />
                  </span>
                  Pix e nota fiscal em outro sistema
                </li>
                <li>
                  <span className="mk no">
                    <X />
                  </span>
                  Cada módulo é uma migração nova
                </li>
                <li>
                  <span className="mk no">
                    <X />
                  </span>
                  Fidelidade e multa para sair
                </li>
              </ul>
            </div>
            <div className="cmp-col op rev" style={{ transitionDelay: '.06s' }}>
              <h3>◆ Operax</h3>
              <ul>
                <li>
                  <span className="mk yes">
                    <Check />
                  </span>
                  Custo da Meta medido e visível na conta
                </li>
                <li>
                  <span className="mk yes">
                    <Check />
                  </span>
                  Pix e NFS-e dentro da própria conversa
                </li>
                <li>
                  <span className="mk yes">
                    <Check />
                  </span>
                  Portal de apps: ativa e usa
                </li>
                <li>
                  <span className="mk yes">
                    <Check />
                  </span>
                  Sem fidelidade. Leva seus dados quando quiser
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FaqSection />
    </>
  );
}

function FaqItem({ q, children, open }: { q: string; children: ReactNode; open?: boolean }) {
  return (
    <details className="faq-i" open={open}>
      <summary>
        {q}
        <Chevron />
      </summary>
      <div className="fa">{children}</div>
    </details>
  );
}

/** Troca a expressão "chat do site" por um botão que abre o chat na página. */
function AnswerWithChatLink({ text, onOpenChat }: { text: string; onOpenChat: () => void }) {
  const alvo = 'chat do site';
  const at = text.indexOf(alvo);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <button type="button" className="faq-inline-link" data-faq-open-chat onClick={onOpenChat}>
        {alvo}
      </button>
      {text.slice(at + alvo.length)}
    </>
  );
}

function FaqSection() {
  const onOpenChat = () => {
    openSupportChat();
  };

  return (
    <section className="pad" style={{ background: 'linear-gradient(180deg,#fff,#f6f9ff)' }}>
      <div className="shell">
        <div className="sec-head center rev">
          <h2 className="h2">Perguntas frequentes</h2>
          <p className="lead" style={{ marginTop: 12, maxWidth: 520, marginInline: 'auto' }}>
            O que o pessoal mais pergunta antes de assinar. Se a sua não estiver aqui, chama no chat ou no WhatsApp.
          </p>
        </div>
        <div className="faq-wrap rev">
          {pricingFaq.map((item, index) => (
            <FaqItem key={item.question} q={item.question} open={index === 0}>
              {item.chatLink ? <AnswerWithChatLink text={item.answer} onOpenChat={onOpenChat} /> : item.answer}
            </FaqItem>
          ))}
        </div>

        <div className="faq-ask rev">
          <p className="faq-ask-copy">Não achou sua pergunta? Fala com a gente.</p>
          <div className="faq-ask-actions">
            <button type="button" className="btn btn-primary" data-faq-open-chat onClick={onOpenChat}>
              Abrir chat
            </button>
            <a
              className="btn btn-ghost"
              data-faq-wa
              href={supportWhatsAppHref()}
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
